import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Card,
  CardContent,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Divider,
  Alert
} from '@mui/material';
import {
  AccountBalance,
  School,
  Assessment
} from '@mui/icons-material';
import { useSchoolFeesMode } from './SchoolFeesContext'; // Import the context

const PerformanceTypeDialog = ({ 
  open, 
  onClose, 
  onSelect, 
  loading = false,
  groupId // Add groupId prop
}) => {
  const [selectedType, setSelectedType] = React.useState('regular');
  const { setModeForGroup } = useSchoolFeesMode(); // Use the context

  const handleSelect = () => {
    const isSchoolFees = selectedType === 'school_fees';
    
    // ✅ Store in context BEFORE calling onSelect
    if (groupId) {
      setModeForGroup(groupId, isSchoolFees);
      console.log('✅ Mode stored in context for group', groupId, ':', isSchoolFees);
    }
    
    onSelect(isSchoolFees);
    handleClose();
  };

  const handleClose = () => {
    setSelectedType('regular');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Assessment color="primary" />
          <Typography variant="h6">
            Select Performance Analysis Type
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          Choose the type of performance analysis you want to generate for this group.
        </Alert>

        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            {/* Regular Accounts Option */}
            <Card 
              variant="outlined" 
              sx={{ 
                mb: 2,
                cursor: 'pointer',
                border: selectedType === 'regular' ? '2px solid' : '1px solid',
                borderColor: selectedType === 'regular' ? 'primary.main' : 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 2
                }
              }}
              onClick={() => setSelectedType('regular')}
            >
              <CardContent>
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <FormControlLabel
                    value="regular"
                    control={<Radio />}
                    label=""
                    sx={{ m: 0 }}
                  />
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <AccountBalance color="primary" />
                      <Typography variant="h6">
                        Regular Accounts
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Standard performance analysis for regular group savings, loans, and advances.
                      This includes LSF (Long-term Savings Fund), loan repayments, and advance tracking.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* School Fees Accounts Option */}
            <Card 
              variant="outlined" 
              sx={{ 
                cursor: 'pointer',
                border: selectedType === 'school_fees' ? '2px solid' : '1px solid',
                borderColor: selectedType === 'school_fees' ? 'secondary.main' : 'divider',
                '&:hover': {
                  borderColor: 'secondary.main',
                  boxShadow: 2
                }
              }}
              onClick={() => setSelectedType('school_fees')}
            >
              <CardContent>
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <FormControlLabel
                    value="school_fees"
                    control={<Radio color="secondary" />}
                    label=""
                    sx={{ m: 0 }}
                  />
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <School color="secondary" />
                      <Typography variant="h6">
                        School Fees Accounts
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Performance analysis specifically for school fees savings groups.
                      Tracks member contributions toward educational expenses and school fee payments.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Note:</strong> The selected type determines how the financial data is calculated 
            and displayed in the performance analysis report.
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          variant="contained"
          onClick={handleSelect}
          disabled={loading}
          color={selectedType === 'school_fees' ? 'secondary' : 'primary'}
        >
          Continue with {selectedType === 'regular' ? 'Regular' : 'School Fees'} Analysis
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PerformanceTypeDialog;